import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import {
  BasinStyle,
  BathroomConfigurationState,
  ColumnConfiguration,
  ConfigurationSection,
  HandleConfiguration,
  HandleLengthOption,
  HandleType,
  HandleTypeOption,
  RowConfiguration,
  SelectedElement,
  UISelectionState,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import { BathroomService } from '../../applications/bathroom.service';
import {
  BasinType,
  BathroomCompositionDetailDto,
  CompositionConfigurationDto,
  CompositionRowDto,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { BathroomFinishingSimpleDto } from 'src/app/models/configurators-2d/common-bathroom-dtos';
import {
  BaseBathroomConfigurationDto,
  BathroomConfigurationProductDto,
  BathroomProductDto,
  InstallationType,
  ProductAttribute,
} from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { BathroomValidationService } from './bathroom-validation.service';
import { BathroomOptionsService, AutoSwitchResult, AvailableOptionsResult } from './bathroom-options.service';
import { BathroomProductService } from './bathroom-product.service';
import { BathroomPriceService } from './bathroom-price.service';
import { Router } from '@angular/router';
import {
  BathroomConfigurationDto,
  BathroomCreateUpdateConfigurationDto,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-configuration-dtos';
import { ProductListItem } from 'src/app/models/product-list/productListItem';
import { PurchaseListItem } from 'src/app/models/purchase-list-item';
import { PurchaseListStore } from 'src/app/stores/purchase-list.store';
import { HasUnsavedChanges } from 'src/app/services/interfaces/has-unsaved-changes.interface';
import { ProductApiService } from 'src/app/services/productApi/product-api.service';
import { GlobalResetService } from 'src/app/services/global-reset.service';
import { isValidBathroomPreviewImage } from './bathroom-preview-capture.util';

@Injectable({
  providedIn: 'root',
})
export class BathroomConfigurationService implements OnDestroy, HasUnsavedChanges {
  private readonly destroy$ = new Subject<void>();

  private readonly _purchaseListStore = inject(PurchaseListStore);
  private readonly _router = inject(Router);

  hasUnsavedChanges(): boolean {
    const state = this.configurationState$.value;
    return state.composition !== null && !state.isLoading;
  }

  // État principal de la configuration
  private readonly configurationState$ = new BehaviorSubject<BathroomConfigurationState>(this.getInitialState());

  // État de sélection UI
  private readonly uiSelectionState$ = new BehaviorSubject<UISelectionState>({
    selectedElement: null,
    activeSection: 'dimensions',
    viewMode: 'compact',
  });

  // Subject pour les messages de basculement automatique (Plan vasque → Vasque à poser)
  private readonly autoSwitchMessage$ = new Subject<AutoSwitchResult>();

  private GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

  // Observables publics
  public readonly configuration$: Observable<BathroomConfigurationState> = this.configurationState$.asObservable();
  public readonly uiSelection$: Observable<UISelectionState> = this.uiSelectionState$.asObservable();

  /**
   * Observable émettant les messages lors d'un basculement automatique
   * Les composants peuvent s'y abonner pour afficher des notifications
   */
  public readonly onAutoSwitch$: Observable<AutoSwitchResult> = this.autoSwitchMessage$.asObservable();

  // Observables dérivés
  // TODO : code tempo, à voir comment ça évolue
  public rangeId: string | null = null;
  public bathroomConfigurationProducts: BathroomConfigurationProductDto[] = [];
  public bathroomConfigurationSceneImage: string | null = null;
  private visiblePreviewCapture: (() => Promise<string | null>) | null = null;
  private fallbackPreviewCapture: (() => Promise<string | null>) | null = null;

  /** Indique si la modal des filtres a déjà été affichée (pour ne pas la réafficher au retour arrière) */
  public hasShownFiltersModal: boolean = false;

  /** Largeur max souhaitée renseignée dans les filtres (null si aucun filtre appliqué) */
  public maxFurnitureWidth: number | null = null;

  // Observables dérivés pour faciliter l'accès
  public readonly composition$ = this.configuration$.pipe(
    map(state => state.composition),
    distinctUntilChanged()
  );

  public readonly totalPrice$ = this.configuration$.pipe(
    map(state => state.totalPrice),
    distinctUntilChanged()
  );

  public readonly isValid$ = this.configuration$.pipe(
    map(state => state.isValid),
    distinctUntilChanged()
  );

  public readonly validationErrors$ = this.configuration$.pipe(
    map(state => state.validationErrors),
    distinctUntilChanged()
  );

  public readonly isLoading$ = this.configuration$.pipe(
    map(state => state.isLoading),
    distinctUntilChanged()
  );

  constructor(
    private readonly bathroomService: BathroomService,
    private readonly validationService: BathroomValidationService,
    private readonly optionsService: BathroomOptionsService,
    private readonly productService: BathroomProductService,
    private readonly priceService: BathroomPriceService,
    private readonly productApiService: ProductApiService,
    private readonly globalResetService: GlobalResetService
  ) {
    this.setupAutoValidation();
    this.globalResetService.register(() => this.resetAll());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Initialisation
  // ============================================

  private getInitialState(): BathroomConfigurationState {
    return {
      composition: null,
      isLoading: false,
      isPreviewLoading: false,
      isSaving: false,
      selectedBasinType: BasinType.Simple,
      selectedBasinStyle: BasinStyle.WashbasinTop,
      selectedGlobalWidth: undefined,
      rowConfigurations: [],
      handleConfiguration: {
        handleType: HandleType.Integrated,
        availableHandleTypes: [],
        availableLengths: [],
        availableFinitions: [],
      },
      validationErrors: [],
      isValid: true,
      totalPrice: 0,
      availableOptions: {
        basinTypes: [],
        basinStyles: [],
        globalWidths: [],
        allowOffset: true,
        dimensionDisplayType: 'global',
        topRowWidths: [],
        bottomRowWidths: [],
      },
    };
  }

  private setupAutoValidation(): void {
    this.configuration$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(state => {
      if (state.composition) {
        const rangeId = state.composition.compositionConfiguration.rangeId;
        const errors = this.validationService.validateConfiguration(state, rangeId);
        if (JSON.stringify(errors) !== JSON.stringify(state.validationErrors)) {
          this.updateState({
            validationErrors: errors,
            isValid: errors.length === 0,
          });
        }
      }
    });
  }

  // ============================================
  // Chargement de la composition
  // ============================================

  async loadComposition(compositionId: string): Promise<void> {
    const savedMaxFurnitureWidth = this.maxFurnitureWidth;
    this.resetAll();
    this.maxFurnitureWidth = savedMaxFurnitureWidth;
    this.updateState({ isLoading: true });

    try {
      const composition = await this.bathroomService.getCompositionById(compositionId);
      console.log('Composition ouverte : ', composition);
      await this.initializeConfiguration(composition);
      // Le style de vasque par défaut est maintenant déterminé automatiquement
      // en fonction des allowedBasinTypes lors de l'initialisation
    } catch (error) {
      console.error('Erreur lors du chargement de la composition', error);
      throw error;
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  async initializeConfiguration(composition: BathroomCompositionDetailDto): Promise<void> {
    const firstRow = composition.compositionConfiguration?.rows?.find(r => r.index === 0);
    const initialBasinType: BasinType = BasinType.Simple;
    const maxFurnitureWidth = this.maxFurnitureWidth;

    // Charger les contraintes une seule fois lors de l'initialisation avec le rangeId
    const rangeId = composition.compositionConfiguration?.rangeId;
    if (rangeId) {
      await this.validationService.loadConstraintsForRange(rangeId);
    }

    // Calculer les options disponibles pour déterminer le style de vasque par défaut
    // Le style sera automatiquement imposé si un seul choix est disponible (basé sur allowedBasinTypes)
    const preliminaryOptions = this.optionsService.calculateAvailableOptions(composition, initialBasinType, undefined);

    // Déterminer le style de vasque initial en fonction des options disponibles
    const initialBasinStyle: BasinStyle = this.optionsService.getDefaultBasinStyle(preliminaryOptions.basinStyles);

    const rowConfigurations = await this.buildRowConfigurations(composition, initialBasinStyle);
    const handleConfiguration = await this.loadHandleOptions(composition);

    const availableOptions = this.optionsService.calculateAvailableOptions(
      composition,
      initialBasinType,
      initialBasinStyle,
      rowConfigurations
    );
    const totalPrice = this.priceService.calculateTotalPrice(composition);

    // Initialiser la dimension globale si dimensionDisplayType est 'global'
    const config = composition.compositionConfiguration;
    let initialGlobalWidth: number | undefined =
      availableOptions.dimensionDisplayType === 'global' && config ? firstRow?.width : undefined;

    // Si la dimension par défaut dépasse la largeur maximale souhaitée, sélectionner
    // automatiquement la plus grande dimension disponible compatible (≤ maxFurnitureWidth)
    if (
      initialGlobalWidth !== undefined &&
      maxFurnitureWidth !== null &&
      initialGlobalWidth > maxFurnitureWidth
    ) {
      const compatibleWidths = availableOptions.globalWidths
        .filter(w => w.available && w.value <= maxFurnitureWidth)
        .map(w => w.value);
      if (compatibleWidths.length > 0) {
        initialGlobalWidth = Math.max(...compatibleWidths);
      }
    }

    this.updateState({
      composition: composition,
      selectedBasinType: initialBasinType,
      selectedBasinStyle: initialBasinStyle,
      selectedGlobalWidth: initialGlobalWidth,
      rowConfigurations,
      handleConfiguration,
      availableOptions,
      totalPrice,
      validationErrors: [],
      isValid: true,
    });
  }

  private async buildRowConfigurations(
    composition: BathroomCompositionDetailDto,
    basinStyle?: BasinStyle
  ): Promise<RowConfiguration[]> {
    const rows = composition.compositionConfiguration?.rows || [];
    const rangeId = composition.compositionConfiguration?.rangeId;
    const config = composition.compositionConfiguration;
    const dimensionDisplayType = config ? this.optionsService.getDimensionDisplayType(config) : 'global';
    const configurations: RowConfiguration[] = [];

    for (const row of rows) {
      let availableCoverFinitions: BathroomFinishingSimpleDto[] = [];
      // Charger les finitions du cover pour la row 0 (seulement en mode "Vasque à poser")
      // ou pour la row 1 si elle a un offset ou des largeurs différentes (cover intermédiaire)
      // Note: En mode "Plan vasque", il n'y a pas de cover séparé (le plan vasque EST le basinProduct)
      const hasCoverForRow0 = row.index === 0 && basinStyle === BasinStyle.CountertopBasin;
      const row0 = rows.find(r => r.index === 0);
      const hasDifferentWidths = row.index === 1 && row0 && row.width !== row0.width;
      const hasCoverForRow1 = row.index === 1 && ((row.offset || 0) !== 0 || hasDifferentWidths);
      if (row.coverProduct && (hasCoverForRow0 || hasCoverForRow1)) {
        availableCoverFinitions = await this.bathroomService.getFinishingsByAttribute(ProductAttribute.ToiletTop);
      }

      const columnConfigurations = await this.buildColumnConfigurations(row, rangeId);
      const offsetConstraints = this.optionsService.calculateOffsetConstraints(row, rows);

      // Calculer les options de dimensions pour cette rangée (si allowOffset = true)
      const availableRowWidths = this.optionsService.calculateRowWidthOptions(composition, row.index);

      configurations.push({
        rowId: row.id,
        rowIndex: row.index,
        selectedWidth: dimensionDisplayType === 'perRow' ? row.width : undefined,
        availableRowWidths,
        offset: row.offset || 0,
        availableOffsets: this.optionsService.calculateAvailableOffsets(row),
        hasOffsetOption: offsetConstraints.hasOffsetOption,
        minOffset: offsetConstraints.minOffset,
        maxOffset: offsetConstraints.maxOffset,
        coverFinitionId: row.coverProduct?.bathroomFinishingId,
        availableCoverFinitions,
        columnConfigurations,
      });
    }

    return configurations;
  }

  private async buildColumnConfigurations(row: CompositionRowDto, rangeId?: string): Promise<ColumnConfiguration[]> {
    const columns = row.columns || [];
    const configurations: ColumnConfiguration[] = [];

    for (const column of columns) {
      let availableFinitions: BathroomFinishingSimpleDto[] = [];
      if (column.product) {
        availableFinitions = await this.bathroomService.getFinishingsByAttribute(ProductAttribute.UnderWashbasin);
      }

      configurations.push({
        columnId: column.id,
        columnIndex: column.index,
        width: column.width,
        finitionId: column.product?.bathroomFinishingId,
        availableFinitions,
        currentProductId: column.productId,
        currentProduct: column.product,
      });
    }

    return configurations;
  }

  private async loadHandleOptions(composition: BathroomCompositionDetailDto): Promise<HandleConfiguration> {
    const handleInfos = composition.compositionConfiguration?.handleConfigurationInfos;
    const availableHandles = handleInfos?.availableHandles ?? [];

    const installationTypeToHandleType = new Map<InstallationType, HandleType>([
      [InstallationType.WallMountedIntegratedHandle, HandleType.Integrated],
      [InstallationType.WallMountedFixedHandle, HandleType.ToFix],
    ]);

    const availableHandleTypes: HandleTypeOption[] = availableHandles
      .filter(h => installationTypeToHandleType.has(h.installationType))
      .map(h => ({
        value: installationTypeToHandleType.get(h.installationType)!,
        label: h.name,
        imageUrl: h.imageUrl,
        available: true,
      }));

    // Récupérer les dimensions possibles depuis la poignée "À fixer"
    const toFixHandle = availableHandles.find(h => h.installationType === InstallationType.WallMountedFixedHandle);
    const availableLengths = this.parsePossibleDimensions(toFixHandle?.possibleDimensions);

    const availableFinitions = await this.bathroomService.getFinishingsByAttribute(ProductAttribute.Handles);

    // Type par défaut : déterminé par l'installationType de la composition, sinon le premier disponible
    const compositionInstallationType = composition.compositionConfiguration?.installationType;
    const mappedDefaultType = compositionInstallationType
      ? installationTypeToHandleType.get(compositionInstallationType)
      : undefined;
    const defaultHandleType =
      mappedDefaultType ?? (availableHandleTypes.length > 0 ? availableHandleTypes[0].value : HandleType.Integrated);

    // Si le type par défaut est "À fixer", sélectionner par défaut la première longueur et la première finition disponibles
    // pour éviter d'avoir une configuration invalide par défaut
    let defaultHandleLength: number | undefined;
    let defaultHandleFinitionId: string | undefined;

    if (defaultHandleType === HandleType.ToFix) {
      defaultHandleLength = availableLengths.find(l => l.available)?.value;
      defaultHandleFinitionId = availableFinitions[0]?.id;
    }

    return {
      handleType: defaultHandleType,
      availableHandleTypes,
      availableLengths,
      availableFinitions,
      handleLength: defaultHandleLength,
      handleFinitionId: defaultHandleFinitionId,
    };
  }

  private parsePossibleDimensions(possibleDimensions?: string): HandleLengthOption[] {
    if (!possibleDimensions) return [];
    try {
      const dimensions: number[] = JSON.parse(possibleDimensions);
      return dimensions.map(d => ({
        value: d,
        label: d.toString(),
        available: true,
      }));
    } catch {
      return [];
    }
  }

  // ============================================
  // Actions de modification
  // ============================================

  /**
   * Vérifie et applique le basculement automatique de Plan vasque vers Vasque à poser si nécessaire
   * @returns true si un basculement a été effectué
   */
  private async checkAndApplyAutoSwitch(composition: BathroomCompositionDetailDto): Promise<boolean> {
    const currentState = this.configurationState$.value;

    // Vérifier si un basculement est nécessaire
    const autoSwitch = this.optionsService.shouldAutoSwitchToCountertopBasin(
      composition,
      currentState.selectedBasinStyle
    );

    if (autoSwitch.shouldSwitch) {
      // Émettre le message pour notification
      this.autoSwitchMessage$.next(autoSwitch);

      // Appliquer le basculement vers "Vasque à poser"
      await this.setBasinStyle(BasinStyle.CountertopBasin);
      return true;
    }

    return false;
  }

  /**
   * Vérifie et corrige automatiquement le type de vasque si nécessaire.
   * Si le type actuel (ex: Double) n'est plus disponible, bascule vers Simple.
   * @param availableOptions Les options disponibles après recalcul
   * @returns true si une correction a été effectuée
   */
  private async checkAndCorrectBasinType(availableOptions: AvailableOptionsResult): Promise<boolean> {
    const currentState = this.configurationState$.value;
    const currentType = currentState.selectedBasinType;

    // Vérifier si le type actuel est toujours valide
    if (this.optionsService.isBasinTypeValid(availableOptions.basinTypes, currentType)) {
      return false;
    }

    // Le type actuel n'est plus valide, déterminer le nouveau type
    const newType = this.optionsService.getDefaultBasinType(availableOptions.basinTypes, currentType);

    if (newType !== currentType) {
      console.log(`Correction automatique du type de vasque: ${currentType} → ${newType}`);
      this.updateState({ selectedBasinType: newType });
      await this.updateBasinProduct(newType);
      return true;
    }

    return false;
  }

  async setBasinType(basinType: BasinType): Promise<void> {
    const currentState = this.configurationState$.value;
    if (!currentState.composition) return;

    const option = currentState.availableOptions.basinTypes.find(opt => opt.value === basinType);
    if (!option?.available) return;

    this.updateState({ selectedBasinType: basinType });
    await this.updateBasinProduct(basinType);
  }

  async setBasinStyle(basinStyle: BasinStyle): Promise<void> {
    const currentState = this.configurationState$.value;
    if (!currentState.composition?.compositionConfiguration) return;

    // Vérifier que le style est dans la liste des options disponibles (filtrées selon allowedBasinTypes)
    const option = currentState.availableOptions.basinStyles.find(opt => opt.value === basinStyle);
    // Si l'option n'existe pas dans la liste filtrée ou n'est pas disponible, ignorer
    if (!option || !option.available) return;

    this.updateState({ selectedBasinStyle: basinStyle, isPreviewLoading: true });

    // D'abord mettre à jour les produits (coverProduct, basinProduct) selon le nouveau style
    // Cela doit être fait AVANT recalculateRowConfigurations pour que le coverProduct existe
    let updatedComposition = await this.productService.updateBasinStyleProducts(currentState.composition, basinStyle);

    if (!updatedComposition) {
      updatedComposition = currentState.composition;
    }

    // Maintenant recalculer les configurations avec la composition mise à jour
    const recalculatedRowConfigurations = await this.recalculateRowConfigurations(
      updatedComposition,
      currentState.rowConfigurations,
      basinStyle
    );

    const availableOptions = this.optionsService.calculateAvailableOptions(
      updatedComposition,
      currentState.selectedBasinType,
      basinStyle,
      recalculatedRowConfigurations
    );

    this.updateState({
      composition: updatedComposition,
      rowConfigurations: recalculatedRowConfigurations,
      availableOptions,
      totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
      isPreviewLoading: false,
    });

    // Vérifier et corriger le type de vasque si nécessaire (ex: Double → Simple)
    await this.checkAndCorrectBasinType(availableOptions);
  }

  async setRowOffset(rowIndex: number, offset: number): Promise<void> {
    const currentState = this.configurationState$.value;
    const rowConfig = currentState.rowConfigurations.find(r => r.rowIndex === rowIndex);

    if (!rowConfig) return;

    // Valider l'offset par rapport aux contraintes min/max
    if (offset < rowConfig.minOffset || offset > rowConfig.maxOffset) return;

    const updatedRowConfigurations = currentState.rowConfigurations.map(r =>
      r.rowIndex === rowIndex ? { ...r, offset } : r
    );

    this.updateState({ rowConfigurations: updatedRowConfigurations });
    await this.updateCompositionRow(rowIndex, { offset });
  }

  async swapNichePosition(rowIndex: number, nicheColumnIndex: number): Promise<void> {
    const currentState = this.configurationState$.value;
    const composition = currentState.composition;
    if (!composition?.compositionConfiguration) return;

    const row = composition.compositionConfiguration.rows.find(r => r.index === rowIndex);
    if (!row) return;

    const sortedColumns = [...row.columns].sort((a, b) => a.index - b.index);
    const maxIndex = sortedColumns[sortedColumns.length - 1].index;
    const isNicheAtLeft = nicheColumnIndex === 0;

    const updatedColumns = row.columns.map(col => {
      if (col.index === nicheColumnIndex) {
        return { ...col, index: isNicheAtLeft ? maxIndex : 0 };
      }
      return { ...col, index: isNicheAtLeft ? col.index - 1 : col.index + 1 };
    });

    let updatedConfig: CompositionConfigurationDto = {
      ...composition.compositionConfiguration,
      rows: composition.compositionConfiguration.rows.map(r =>
        r.index === rowIndex ? { ...r, columns: updatedColumns } : r
      ),
    };

    // Mettre à jour allowedBasinTypes et hasElementRight selon la nouvelle position de la niche (ligne du haut uniquement)
    if (rowIndex === 0) {
      const newNicheIsAtLeft = !isNicheAtLeft;
      updatedConfig = {
        ...updatedConfig,
        allowedBasinTypes: newNicheIsAtLeft ? JSON.stringify([7]) : JSON.stringify([1, 7]),
        hasElementRight: !newNicheIsAtLeft,
      };
    }

    const updatedComposition: BathroomCompositionDetailDto = {
      ...composition,
      compositionConfiguration: updatedConfig,
    };

    const rowConfigurations = await this.buildRowConfigurations(updatedComposition, currentState.selectedBasinStyle);
    const availableOptions = this.optionsService.calculateAvailableOptions(
      updatedComposition,
      currentState.selectedBasinType,
      currentState.selectedBasinStyle,
      rowConfigurations
    );

    this.updateState({
      composition: updatedComposition,
      rowConfigurations,
      availableOptions,
      totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
    });

    await this.checkAndApplyAutoSwitch(updatedComposition);
  }

  async setColumnWidth(rowIndex: number, columnIndex: number, width: number): Promise<void> {
    const currentState = this.configurationState$.value;
    const rowConfig = currentState.rowConfigurations.find(r => r.rowIndex === rowIndex);
    const columnConfig = rowConfig?.columnConfigurations.find(c => c.columnIndex === columnIndex);

    if (!columnConfig) return;

    // Mettre à jour la configuration locale
    const updatedRowConfigurations = currentState.rowConfigurations.map(r => {
      if (r.rowIndex !== rowIndex) return r;
      return {
        ...r,
        columnConfigurations: r.columnConfigurations.map(c => (c.columnIndex === columnIndex ? { ...c, width } : c)),
      };
    });

    this.updateState({ rowConfigurations: updatedRowConfigurations });

    // Rechercher et mettre à jour le produit
    const updatedComposition = await this.productService.updateColumnProduct(
      currentState.composition!,
      updatedRowConfigurations,
      rowIndex,
      columnIndex,
      { width }
    );

    if (updatedComposition && rowIndex === 0) {
      const firstRow = updatedComposition.compositionConfiguration?.rows?.find(r => r.index === 0);
      if (firstRow) {
        const newTotalWidth = firstRow.columns.reduce((sum, col) => sum + col.width, 0);

        const updatedRows = updatedComposition.compositionConfiguration!.rows.map((row, idx) =>
          idx === 0 ? { ...row, width: newTotalWidth } : row
        );

        let finalComposition: BathroomCompositionDetailDto = {
          ...updatedComposition,
          compositionConfiguration: {
            ...updatedComposition.compositionConfiguration!,
            rows: updatedRows,
          },
        };

        const availableOptions = this.optionsService.calculateAvailableOptions(
          finalComposition,
          currentState.selectedBasinType,
          currentState.selectedBasinStyle
        );

        // Mettre à jour le plan de toilette/vasque avec la nouvelle largeur
        const topUpdatedComposition = await this.productService.updateTopProductForNewWidth(
          finalComposition,
          newTotalWidth,
          currentState.selectedBasinStyle
        );

        if (topUpdatedComposition) {
          finalComposition = topUpdatedComposition;

          // S'assurer que la largeur de la ligne est bien conservée (au cas où le service renverrait une ancienne valeur)
          const rows = finalComposition.compositionConfiguration?.rows;
          const row0 = rows?.find(r => r.index === 0);
          if (row0 && row0.width !== newTotalWidth) {
            finalComposition = {
              ...finalComposition,
              compositionConfiguration: {
                ...finalComposition.compositionConfiguration!,
                rows: rows!.map(r => (r.index === 0 ? { ...r, width: newTotalWidth } : r)),
              },
            };
          }
        }

        const recalculatedRowConfigurations = await this.recalculateRowConfigurations(
          finalComposition,
          updatedRowConfigurations,
          currentState.selectedBasinStyle
        );

        this.updateState({
          composition: finalComposition,
          rowConfigurations: recalculatedRowConfigurations,
          availableOptions,
          totalPrice: this.priceService.calculateTotalPrice(finalComposition),
        });
      }
    }
  }

  async setColumnFinition(rowIndex: number, columnIndex: number, bathroomFinishingId: string): Promise<void> {
    const currentState = this.configurationState$.value;

    const updatedRowConfigurations = currentState.rowConfigurations.map(r => {
      if (r.rowIndex !== rowIndex) return r;
      return {
        ...r,
        columnConfigurations: r.columnConfigurations.map(c =>
          c.columnIndex === columnIndex ? { ...c, finitionId: bathroomFinishingId } : c
        ),
      };
    });

    this.updateState({ rowConfigurations: updatedRowConfigurations, isPreviewLoading: true });

    // Récupérer l'installationType actuel du produit dans la composition
    // pour préserver le type de poignée (intégrée vs à fixer)
    const currentRow = currentState.composition?.compositionConfiguration?.rows.find(r => r.index === rowIndex);
    const currentColumn = currentRow?.columns.find(c => c.index === columnIndex);
    const currentInstallationType = currentColumn?.product?.installationType;

    const updatedComposition = await this.productService.updateColumnProduct(
      currentState.composition!,
      updatedRowConfigurations,
      rowIndex,
      columnIndex,
      { bathroomFinishingId, installationType: currentInstallationType }
    );

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  async setCoverFinition(rowIndex: number, finitionId: string): Promise<void> {
    const currentState = this.configurationState$.value;

    const updatedRowConfigurations = currentState.rowConfigurations.map(r =>
      r.rowIndex === rowIndex ? { ...r, coverFinitionId: finitionId } : r
    );

    this.updateState({ rowConfigurations: updatedRowConfigurations, isPreviewLoading: true });

    const updatedComposition = await this.productService.updateCoverProduct(
      currentState.composition!,
      rowIndex,
      finitionId
    );

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  async setHandleType(handleType: HandleType): Promise<void> {
    const currentState = this.configurationState$.value;

    let handleLength: number | undefined;
    let handleFinitionId: string | undefined;

    if (handleType === HandleType.Integrated) {
      // Poignée intégrée : pas de longueur ni de finition
      handleLength = undefined;
      handleFinitionId = undefined;
    } else {
      // Poignée à fixer : utiliser les valeurs existantes ou les premières disponibles par défaut
      handleLength =
        currentState.handleConfiguration.handleLength ??
        currentState.handleConfiguration.availableLengths.find(l => l.available)?.value;
      handleFinitionId =
        currentState.handleConfiguration.handleFinitionId ?? currentState.handleConfiguration.availableFinitions[0]?.id;
    }

    const updatedHandleConfig: HandleConfiguration = {
      ...currentState.handleConfiguration,
      handleType,
      handleLength,
      handleFinitionId,
    };

    this.updateState({ handleConfiguration: updatedHandleConfig, isPreviewLoading: true });

    const updatedComposition = await this.productService.updateAllHandles(
      currentState.composition!,
      updatedHandleConfig
    );

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  async setHandleLength(length: number): Promise<void> {
    const currentState = this.configurationState$.value;

    if (currentState.handleConfiguration.handleType !== HandleType.ToFix) return;

    const updatedHandleConfig: HandleConfiguration = {
      ...currentState.handleConfiguration,
      handleLength: length,
    };

    this.updateState({ handleConfiguration: updatedHandleConfig, isPreviewLoading: true });

    const updatedComposition = await this.productService.updateAllHandles(
      currentState.composition!,
      updatedHandleConfig
    );

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  async setHandleFinition(finitionId: string): Promise<void> {
    const currentState = this.configurationState$.value;

    if (currentState.handleConfiguration.handleType !== HandleType.ToFix) return;

    const updatedHandleConfig: HandleConfiguration = {
      ...currentState.handleConfiguration,
      handleFinitionId: finitionId,
    };

    this.updateState({ handleConfiguration: updatedHandleConfig, isPreviewLoading: true });

    const updatedComposition = await this.productService.updateAllHandles(
      currentState.composition!,
      updatedHandleConfig
    );

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  /**
   * Vérifie si la finition de colonne peut être appliquée à tous les éléments
   * @param finitionId L'ID de la finition à appliquer
   * @returns true si au moins un autre élément a une finition différente et compatible
   */
  canApplyColumnFinitionToAll(finitionId: string): boolean {
    const currentState = this.configurationState$.value;

    // Parcourir toutes les colonnes pour voir si au moins une a une finition différente
    for (const rowConfig of currentState.rowConfigurations) {
      for (const colConfig of rowConfig.columnConfigurations) {
        // Vérifier si la colonne a des finitions disponibles
        if (colConfig.availableFinitions.length === 0) continue;

        // Vérifier si la finition est compatible avec cette colonne
        const isCompatible = colConfig.availableFinitions.some(f => f.id === finitionId);
        if (!isCompatible) continue;

        // Vérifier si la finition actuelle est différente
        if (colConfig.finitionId !== finitionId) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Applique la finition de colonne à toutes les colonnes compatibles
   * @param finitionId L'ID de la finition à appliquer
   */
  async applyColumnFinitionToAll(finitionId: string): Promise<void> {
    const currentState = this.configurationState$.value;

    // Mettre à jour toutes les colonnes compatibles
    let updatedRowConfigurations = [...currentState.rowConfigurations];
    const columnsToUpdate: Array<{ rowIndex: number; columnIndex: number }> = [];

    for (const rowConfig of currentState.rowConfigurations) {
      for (const colConfig of rowConfig.columnConfigurations) {
        // Vérifier si la colonne a des finitions disponibles
        if (colConfig.availableFinitions.length === 0) continue;

        // Vérifier si la finition est compatible avec cette colonne
        const isCompatible = colConfig.availableFinitions.some(f => f.id === finitionId);
        if (!isCompatible) continue;

        // Vérifier si la finition actuelle est différente
        if (colConfig.finitionId !== finitionId) {
          columnsToUpdate.push({
            rowIndex: rowConfig.rowIndex,
            columnIndex: colConfig.columnIndex,
          });
        }
      }
    }

    // Mettre à jour la configuration locale
    updatedRowConfigurations = updatedRowConfigurations.map(r => ({
      ...r,
      columnConfigurations: r.columnConfigurations.map(c => {
        const shouldUpdate = columnsToUpdate.some(
          col => col.rowIndex === r.rowIndex && col.columnIndex === c.columnIndex
        );
        if (shouldUpdate) {
          return { ...c, finitionId };
        }
        return c;
      }),
    }));

    this.updateState({ rowConfigurations: updatedRowConfigurations, isPreviewLoading: true });

    // Mettre à jour chaque produit dans la composition
    let updatedComposition = currentState.composition;

    for (const col of columnsToUpdate) {
      updatedComposition = await this.productService.updateColumnProduct(
        updatedComposition!,
        updatedRowConfigurations,
        col.rowIndex,
        col.columnIndex,
        { bathroomFinishingId: finitionId }
      );
    }

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  /**
   * Vérifie si la finition de cover peut être appliquée à tous les covers
   * @param finitionId L'ID de la finition à appliquer
   * @returns true si au moins un autre cover a une finition différente et compatible
   */
  canApplyCoverFinitionToAll(finitionId: string): boolean {
    const currentState = this.configurationState$.value;

    // Parcourir toutes les rows pour voir si au moins une a une finition de cover différente
    for (const rowConfig of currentState.rowConfigurations) {
      // Vérifier si la row a des finitions de cover disponibles
      if (!rowConfig.availableCoverFinitions || rowConfig.availableCoverFinitions.length === 0) continue;

      // Vérifier si la finition est compatible avec ce cover
      const isCompatible = rowConfig.availableCoverFinitions.some(f => f.id === finitionId);
      if (!isCompatible) continue;

      // Vérifier si la finition actuelle est différente
      if (rowConfig.coverFinitionId !== finitionId) {
        return true;
      }
    }

    return false;
  }

  /**
   * Applique la finition de cover à tous les covers compatibles
   * @param finitionId L'ID de la finition à appliquer
   */
  async applyCoverFinitionToAll(finitionId: string): Promise<void> {
    const currentState = this.configurationState$.value;

    // Identifier les rows à mettre à jour
    const rowsToUpdate: number[] = [];

    for (const rowConfig of currentState.rowConfigurations) {
      // Vérifier si la row a des finitions de cover disponibles
      if (!rowConfig.availableCoverFinitions || rowConfig.availableCoverFinitions.length === 0) continue;

      // Vérifier si la finition est compatible avec ce cover
      const isCompatible = rowConfig.availableCoverFinitions.some(f => f.id === finitionId);
      if (!isCompatible) continue;

      // Vérifier si la finition actuelle est différente
      if (rowConfig.coverFinitionId !== finitionId) {
        rowsToUpdate.push(rowConfig.rowIndex);
      }
    }

    // Mettre à jour la configuration locale
    const updatedRowConfigurations = currentState.rowConfigurations.map(r => {
      if (rowsToUpdate.includes(r.rowIndex)) {
        return { ...r, coverFinitionId: finitionId };
      }
      return r;
    });

    this.updateState({ rowConfigurations: updatedRowConfigurations, isPreviewLoading: true });

    // Mettre à jour chaque cover dans la composition
    let updatedComposition = currentState.composition;

    for (const rowIndex of rowsToUpdate) {
      updatedComposition = await this.productService.updateCoverProduct(updatedComposition!, rowIndex, finitionId);
    }

    if (updatedComposition) {
      this.updateState({
        composition: updatedComposition,
        totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        isPreviewLoading: false,
      });
    } else {
      this.updateState({ isPreviewLoading: false });
    }
  }

  /**
   * Définit la dimension globale (quand dimensionDisplayType = 'global')
   * Met à jour toutes les rangées et colonnes avec la nouvelle dimension
   */
  async setGlobalWidth(width: number): Promise<void> {
    const currentState = this.configurationState$.value;
    if (!currentState.composition?.compositionConfiguration) return;

    // Vérifier que dimensionDisplayType est 'global'
    if (currentState.availableOptions.dimensionDisplayType !== 'global') return;

    // Vérifier que la dimension est disponible
    const widthOption = currentState.availableOptions.globalWidths.find(w => w.value === width);
    if (!widthOption?.available) return;

    this.updateState({ selectedGlobalWidth: width, isPreviewLoading: true });

    // Mettre à jour toutes les rangées avec la nouvelle dimension
    let updatedComposition = currentState.composition;

    for (const row of updatedComposition.compositionConfiguration!.rows) {
      updatedComposition = await this.updateRowWidth(updatedComposition, row.index, width);
    }

    // Recalculer les configurations de rangées
    const recalculatedRowConfigurations = await this.buildRowConfigurations(
      updatedComposition,
      currentState.selectedBasinStyle
    );

    // Recalculer les options disponibles
    const availableOptions = this.optionsService.calculateAvailableOptions(
      updatedComposition,
      currentState.selectedBasinType,
      currentState.selectedBasinStyle,
      recalculatedRowConfigurations
    );

    this.updateState({
      composition: updatedComposition,
      rowConfigurations: recalculatedRowConfigurations,
      availableOptions,
      totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
      isPreviewLoading: false,
    });

    // Vérifier si un basculement automatique est nécessaire après le changement de dimension
    await this.checkAndApplyAutoSwitch(updatedComposition);

    // Vérifier et corriger le type de vasque si nécessaire (ex: Double → Simple)
    await this.checkAndCorrectBasinType(availableOptions);
  }

  /**
   * Définit la dimension d'une rangée spécifique (quand dimensionDisplayType = 'perRow')
   */
  async setRowWidth(rowIndex: number, width: number): Promise<void> {
    const currentState = this.configurationState$.value;
    if (!currentState.composition?.compositionConfiguration) return;

    // Vérifier que dimensionDisplayType est 'perRow'
    if (currentState.availableOptions.dimensionDisplayType !== 'perRow') return;

    // Mettre à jour la configuration locale
    let updatedRowConfigurations = currentState.rowConfigurations.map(r =>
      r.rowIndex === rowIndex ? { ...r, selectedWidth: width } : r
    );

    this.updateState({ rowConfigurations: updatedRowConfigurations, isPreviewLoading: true });

    // Mettre à jour la composition avec les nouvelles dimensions
    let updatedComposition = await this.updateRowWidth(currentState.composition, rowIndex, width);

    // Si on change la dimension du haut (row 0), vérifier et ajuster la dimension du bas si nécessaire
    if (rowIndex === 0 && currentState.availableOptions.widthCombinations) {
      const bottomRowConfig = updatedRowConfigurations.find(r => r.rowIndex === 1);
      const currentBottomWidth = bottomRowConfig?.selectedWidth;

      if (currentBottomWidth !== undefined) {
        // Récupérer les dimensions compatibles pour le bas avec la nouvelle dimension du haut
        const compatibleBottomWidths = currentState.availableOptions.widthCombinations.get(width);

        // Vérifier si la dimension actuelle du bas est encore compatible
        if (compatibleBottomWidths && !compatibleBottomWidths.includes(currentBottomWidth)) {
          // Construire les options de largeur du bas pour trouver la plus proche
          const bottomWidthOptions = compatibleBottomWidths.map(w => ({ value: w, available: true }));
          const closestWidth = this.optionsService.findClosestAvailableWidth(currentBottomWidth, bottomWidthOptions);

          if (closestWidth !== undefined && closestWidth !== currentBottomWidth) {
            console.log(`Correction automatique de la dimension du bas: ${currentBottomWidth} → ${closestWidth}`);

            // Mettre à jour la dimension du bas
            updatedRowConfigurations = updatedRowConfigurations.map(r =>
              r.rowIndex === 1 ? { ...r, selectedWidth: closestWidth } : r
            );

            // Mettre à jour la composition avec la nouvelle dimension du bas
            updatedComposition = await this.updateRowWidth(updatedComposition, 1, closestWidth);
          }
        }
      }
    }

    // Recalculer les configurations de rangées
    const recalculatedRowConfigurations = await this.buildRowConfigurations(
      updatedComposition,
      currentState.selectedBasinStyle
    );

    // Fusionner les selectedWidth de updatedRowConfigurations dans recalculatedRowConfigurations
    // pour préserver les valeurs explicitement définies par l'utilisateur
    const finalRowConfigurations = recalculatedRowConfigurations.map(recalcRow => {
      const updatedRow = updatedRowConfigurations.find(r => r.rowIndex === recalcRow.rowIndex);
      return {
        ...recalcRow,
        selectedWidth: updatedRow?.selectedWidth ?? recalcRow.selectedWidth,
      };
    });

    // Recalculer les options disponibles
    const availableOptions = this.optionsService.calculateAvailableOptions(
      updatedComposition,
      currentState.selectedBasinType,
      currentState.selectedBasinStyle,
      finalRowConfigurations
    );

    this.updateState({
      composition: updatedComposition,
      rowConfigurations: finalRowConfigurations,
      availableOptions,
      totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
      isPreviewLoading: false,
    });

    // Vérifier si un basculement automatique est nécessaire (uniquement pour la première rangée)
    if (rowIndex === 0) {
      await this.checkAndApplyAutoSwitch(updatedComposition);

      // Vérifier et corriger le type de vasque si nécessaire (ex: Double → Simple)
      await this.checkAndCorrectBasinType(availableOptions);
    }
  }

  /**
   * Met à jour la dimension d'une rangée et ses colonnes
   */
  private async updateRowWidth(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    newWidth: number
  ): Promise<BathroomCompositionDetailDto> {
    const row = composition.compositionConfiguration?.rows.find(r => r.index === rowIndex);
    if (!row) return composition;

    // Séparer les colonnes redimensionnables et les colonnes fixes (niches + hasElementFix)
    const resizableColumns = row.columns.filter(
      c => c.product?.attribute !== ProductAttribute.Niche && !c.hasElementFix
    );
    const fixedColumns = row.columns.filter(c => c.product?.attribute === ProductAttribute.Niche || c.hasElementFix);

    // Calculer la largeur totale des éléments fixes (ils gardent leur largeur fixe)
    const totalFixedWidth = fixedColumns.reduce((sum, c) => sum + c.width, 0);

    // Largeur disponible pour les colonnes redimensionnables = nouvelle largeur - largeur des éléments fixes
    const availableWidthForDrawers = newWidth - totalFixedWidth;

    // Calculer la largeur par tiroir redimensionnable (diviser si plusieurs)
    const drawerCount = resizableColumns.length;
    const drawerWidth = drawerCount > 0 ? availableWidthForDrawers / drawerCount : availableWidthForDrawers;

    // Mettre à jour chaque tiroir avec la nouvelle largeur
    let updatedComposition = composition;

    // Déterminer l'installationType en fonction du type de poignée actuel
    const handleType = this.configurationState$.value.handleConfiguration.handleType;
    const installationType =
      handleType === HandleType.Integrated
        ? InstallationType.WallMountedIntegratedHandle
        : InstallationType.WallMountedFixedHandle;

    for (const column of resizableColumns) {
      // Mettre à jour le produit de la colonne avec la nouvelle largeur et le bon installationType
      updatedComposition =
        (await this.productService.updateColumnProduct(
          updatedComposition,
          this.configurationState$.value.rowConfigurations,
          rowIndex,
          column.index,
          { width: drawerWidth, installationType }
        )) ?? updatedComposition;
    }

    // Mettre à jour la largeur totale de la rangée
    const updatedRows = updatedComposition.compositionConfiguration!.rows.map(r =>
      r.index === rowIndex ? { ...r, width: newWidth } : r
    );

    updatedComposition = {
      ...updatedComposition,
      compositionConfiguration: {
        ...updatedComposition.compositionConfiguration!,
        rows: updatedRows,
      },
    };

    // Mettre à jour le plan de toilette/vasque si c'est la première rangée
    if (rowIndex === 0) {
      const topUpdatedComposition = await this.productService.updateTopProductForNewWidth(
        updatedComposition,
        newWidth,
        this.configurationState$.value.selectedBasinStyle
      );
      if (topUpdatedComposition) {
        updatedComposition = topUpdatedComposition;

        // S'assurer que la largeur de la ligne est bien conservée
        const rows = updatedComposition.compositionConfiguration?.rows;
        const row0 = rows?.find(r => r.index === rowIndex);
        if (row0 && row0.width !== newWidth) {
          updatedComposition = {
            ...updatedComposition,
            compositionConfiguration: {
              ...updatedComposition.compositionConfiguration!,
              rows: rows!.map(r => (r.index === rowIndex ? { ...r, width: newWidth } : r)),
            },
          };
        }
      }
    }

    // Mettre à jour le plan de toilette intermédiaire si c'est la deuxième rangée (row 1) avec un offset
    if (rowIndex === 1) {
      const updatedRow = updatedComposition.compositionConfiguration?.rows.find(r => r.index === 1);
      // Si la row a un coverProduct (plan de toilette intermédiaire), le mettre à jour avec la nouvelle largeur
      if (updatedRow?.coverProduct) {
        const finishingId = updatedRow.coverProduct.bathroomFinishingId;
        const coverUpdatedComposition = await this.productService.updateCoverProduct(
          updatedComposition,
          1,
          finishingId ?? ''
        );
        if (coverUpdatedComposition) {
          updatedComposition = coverUpdatedComposition;

          // S'assurer que la largeur de la ligne est bien conservée
          const rows = updatedComposition.compositionConfiguration?.rows;
          const row1 = rows?.find(r => r.index === 1);
          if (row1 && row1.width !== newWidth) {
            updatedComposition = {
              ...updatedComposition,
              compositionConfiguration: {
                ...updatedComposition.compositionConfiguration!,
                rows: rows!.map(r => (r.index === 1 ? { ...r, width: newWidth } : r)),
              },
            };
          }
        }
      }
    }

    return updatedComposition;
  }

  // ============================================
  // Méthodes privées de mise à jour
  // ============================================

  private async updateBasinProduct(basinType: BasinType): Promise<void> {
    const currentState = this.configurationState$.value;
    if (!currentState.composition?.compositionConfiguration) return;

    const firstRow = currentState.composition.compositionConfiguration.rows.find(r => r.index === 0);
    if (!firstRow) return;

    firstRow.basinType = basinType;

    try {
      const products = await this.productService.searchBasinProduct(currentState.selectedBasinStyle, firstRow.width);
      if (products.length > 0) {
        const updatedComposition = this.productService.applyProductToBasin(currentState.composition, 0, products[0]);
        this.updateState({
          composition: updatedComposition,
          totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
        });
      }
    } catch (error) {
      console.error('Erreur lors de la recherche du produit vasque', error);
    }
  }

  private async updateCompositionRow(rowIndex: number, changes: Partial<CompositionRowDto>): Promise<void> {
    const currentState = this.configurationState$.value;
    if (!currentState.composition?.compositionConfiguration) return;

    const updatedRows = currentState.composition.compositionConfiguration.rows.map(row =>
      row.index === rowIndex ? { ...row, ...changes } : row
    );

    const updatedComposition: BathroomCompositionDetailDto = {
      ...currentState.composition,
      compositionConfiguration: {
        ...currentState.composition.compositionConfiguration,
        rows: updatedRows,
      },
    };

    this.updateState({
      composition: updatedComposition,
      totalPrice: this.priceService.calculateTotalPrice(updatedComposition),
    });
  }

  private async recalculateRowConfigurations(
    composition: BathroomCompositionDetailDto,
    currentRowConfigs: RowConfiguration[],
    basinStyle: BasinStyle
  ): Promise<RowConfiguration[]> {
    const rows = composition.compositionConfiguration?.rows || [];
    const rangeId = composition.compositionConfiguration?.rangeId;

    return Promise.all(
      currentRowConfigs.map(async rowConfig => {
        const row = rows.find(r => r.index === rowConfig.rowIndex);
        if (!row) return rowConfig;

        const updatedColumnConfigs = rowConfig.columnConfigurations.map(colConfig => {
          const column = row.columns.find(c => c.index === colConfig.columnIndex);
          if (!column) return colConfig;

          return {
            ...colConfig,
            width: column.width,
          };
        });

        // Recharger les finitions du cover si le produit cover existe
        // Note: En mode "Plan vasque", il n'y a pas de cover séparé (le plan vasque EST le basinProduct)
        let availableCoverFinitions: BathroomFinishingSimpleDto[] = [];
        const hasCoverForRow0 = row.index === 0 && basinStyle === BasinStyle.CountertopBasin;
        const row0 = rows.find(r => r.index === 0);
        const hasDifferentWidths = row.index === 1 && row0 && row.width !== row0.width;
        const hasCoverForRow1 = row.index === 1 && ((row.offset || 0) !== 0 || hasDifferentWidths);
        if (row.coverProduct && (hasCoverForRow0 || hasCoverForRow1)) {
          availableCoverFinitions = await this.bathroomService.getFinishingsByAttribute(ProductAttribute.ToiletTop);
        }

        return {
          ...rowConfig,
          columnConfigurations: updatedColumnConfigs,
          availableCoverFinitions,
        };
      })
    );
  }

  // ============================================
  // Gestion de la sélection UI
  // ============================================

  selectElement(element: SelectedElement | null): void {
    this.uiSelectionState$.next({
      ...this.uiSelectionState$.value,
      selectedElement: element,
    });
  }

  setActiveSection(section: ConfigurationSection): void {
    this.uiSelectionState$.next({
      ...this.uiSelectionState$.value,
      activeSection: section,
    });
  }

  toggleViewMode(): void {
    const current = this.uiSelectionState$.value;
    this.uiSelectionState$.next({
      ...current,
      viewMode: current.viewMode === 'compact' ? 'expanded' : 'compact',
    });
  }

  // ============================================
  // Utilitaires
  // ============================================

  private updateState(partialState: Partial<BathroomConfigurationState>): void {
    this.configurationState$.next({
      ...this.configurationState$.value,
      ...partialState,
    });
  }

  getCurrentConfiguration(): BathroomConfigurationState {
    return this.configurationState$.value;
  }

  public async restoreConfigurationFromTunnel(configuration: BaseBathroomConfigurationDto): Promise<void> {
    if (!configuration?.compositionConfiguration || !configuration.compositionId) return;

    const composition: BathroomCompositionDetailDto = {
      id: configuration.compositionId,
      name: configuration.compositionName ?? '',
      thumbnailUrl: configuration.compositionThumbnailUrl ?? '',
      compositionConfigurationId: configuration.compositionConfiguration.id,
      compositionConfiguration: configuration.compositionConfiguration,
    };

    await this.initializeConfiguration(composition);
    this.bathroomConfigurationProducts = configuration.products ?? [];
    this.bathroomConfigurationSceneImage = configuration.thumbnail?.data ?? null;
  }

  public registerVisiblePreviewCapture(capture: () => Promise<string | null>): () => void {
    this.visiblePreviewCapture = capture;
    return () => {
      if (this.visiblePreviewCapture === capture) {
        this.visiblePreviewCapture = null;
      }
    };
  }

  public registerFallbackPreviewCapture(capture: () => Promise<string | null>): () => void {
    this.fallbackPreviewCapture = capture;
    return () => {
      if (this.fallbackPreviewCapture === capture) {
        this.fallbackPreviewCapture = null;
      }
    };
  }

  public hasVisiblePreviewCapture(): boolean {
    return this.visiblePreviewCapture != null;
  }

  public async captureVisiblePreviewImage(): Promise<boolean> {
    const imageData = await this.visiblePreviewCapture?.() ?? await this.fallbackPreviewCapture?.() ?? null;
    if (!isValidBathroomPreviewImage(imageData)) return false;

    this.bathroomConfigurationSceneImage = imageData;
    return true;
  }

  public getValidSceneImage(): string | null {
    return isValidBathroomPreviewImage(this.bathroomConfigurationSceneImage)
      ? this.bathroomConfigurationSceneImage
      : null;
  }

  resetConfiguration(): void {
    this.configurationState$.next(this.getInitialState());
    this.uiSelectionState$.next({
      selectedElement: null,
      activeSection: 'dimensions',
      viewMode: 'compact',
    });

    this.bathroomConfigurationProducts = [];
    this.bathroomConfigurationSceneImage = null;
  }

  resetAll(): void {
    this.resetConfiguration();
    this.rangeId = null;
    this.hasShownFiltersModal = false;
    this.maxFurnitureWidth = null;
  }

  /**
   * Exporte la configuration actuelle pour sauvegarde
   */
  exportConfiguration(): object {
    const state = this.configurationState$.value;
    return {
      compositionId: state.composition?.id,
      selectedBasinType: state.selectedBasinType,
      selectedBasinStyle: state.selectedBasinStyle,
      rowConfigurations: state.rowConfigurations.map(r => ({
        rowIndex: r.rowIndex,
        offset: r.offset,
        coverFinitionId: r.coverFinitionId,
        columns: r.columnConfigurations.map(c => ({
          columnIndex: c.columnIndex,
          width: c.width,
          finitionId: c.finitionId,
        })),
      })),
      handleConfiguration: {
        handleType: state.handleConfiguration.handleType,
        handleLength: state.handleConfiguration.handleLength,
        handleFinitionId: state.handleConfiguration.handleFinitionId,
      },
      totalPrice: state.totalPrice,
      timestamp: new Date().toISOString(),
    };
  }
  /**
   * Mappe les produits de la composition vers bathroomConfigurationProducts
   * Gère le remplacement par doubleProduct si applicable
   */
  async mapCompositionProductsToBathroomConfigurationProducts(): Promise<void> {
    const rows = this.configurationState$.value.composition?.compositionConfiguration?.rows;
    if (!rows) {
      this.bathroomConfigurationProducts = [];
      return;
    }

    const productsByEan = new Map<string, BathroomConfigurationProductDto>();
    const doubleProductReplacements = this.getDoubleProductReplacements(rows);
    const processedDoubleProducts = new Set<string>();

    for (const row of rows) {
      // Produits fixes (cover, vasque)
      // On ajoute les plans de toilette que s'il s'agit du tiroir du haut ou le tiroir du bas à condition qu'il y est un décalage
      if (row.coverProduct && (row.index === 0 || (row.index === 1 && row.offset !== 0))) {
        this.addOrUpdateProduct(productsByEan, row.coverProduct);
      }

      if (row.basinProduct) {
        this.addOrUpdateProduct(productsByEan, row.basinProduct);

        if (row.basinType === BasinType.Double && row.basinProduct.attribute === ProductAttribute.CountertopBasin) {
          this.addOrUpdateProduct(productsByEan, row.basinProduct); // On ajoute à nouveau la vasque pour créer une double vasque à poser
        }
      }

      // Colonnes et poignées
      for (const column of row.columns ?? []) {
        if (column.product) {
          const doubleProduct = doubleProductReplacements.get(column.product.ean);
          if (doubleProduct && !processedDoubleProducts.has(doubleProduct.ean)) {
            this.addOrUpdateProduct(productsByEan, doubleProduct);
            processedDoubleProducts.add(doubleProduct.ean);
          } else if (!doubleProduct) {
            this.addOrUpdateProduct(productsByEan, column.product);
          }
        }
        if (column.handle) this.addOrUpdateProduct(productsByEan, column.handle);
      }
    }

    this.bathroomConfigurationProducts = Array.from(productsByEan.values());

    // Résoudre les images manquantes ou cassées
    await this.resolveProductImages(this.bathroomConfigurationProducts);
  }

  public async buildConfigurationProducts(): Promise<BathroomConfigurationProductDto[]> {
    await this.mapCompositionProductsToBathroomConfigurationProducts();
    return this.bathroomConfigurationProducts;
  }

  /**
   * Envoie les produits vers la liste d'achat et configure le bouton de validation
   */
  async sendToPurchaseList(): Promise<void> {
    const builtConfigurationProducts = await this.buildConfigurationProducts();
    const compositionEans = this.getCompositionRelatedEans();
    const configurationEans = new Set(builtConfigurationProducts.map(p => p.ean));

    // Garder uniquement les produits ajoutés manuellement (ni de la config, ni liés à la composition)
    const manuallyAddedProducts = this._purchaseListStore
      .products()
      .filter(item => !configurationEans.has(item.product.ean) && !compositionEans.has(item.product.ean));

    // Convertir et merger les produits
    const configurationProducts = builtConfigurationProducts.map(p => this.toPurchaseListItem(p));
    configurationProducts.forEach(p => p.wasManuallyAdded = false);
    this._purchaseListStore.products.set([...configurationProducts, ...manuallyAddedProducts]);
    this._purchaseListStore.onPurchaseListUpdated();

    // Configurer le bouton personnalisé
    const compositionId = this.configurationState$.value.composition?.id;
    this._purchaseListStore.customActionButton.set({
      label: 'Valider ma configuration',
      action: () => {
        this.syncProductsFromPurchaseList();
        this._router.navigate(['/salle-de-bain-mobile/configuration', compositionId, 'fin']);
      },
    });
    this._purchaseListStore.setAddAnotherProductLabel('Ajouter un produit complémentaire');

    this._router.navigate(['/liste-achat']);
  }

  /**
   * Retourne les EANs des produits pouvant être remplacés par leur doubleProduct
   * Conditions : 2 lignes sans offset, même EAN au même index de colonne, doubleProduct disponible
   */
  private getDoubleProductReplacements(rows: CompositionRowDto[]): Map<string, BathroomProductDto> {
    const replacements = new Map<string, BathroomProductDto>();
    const firstRow = rows.find(r => r.index === 0);
    const secondRow = rows.find(r => r.index === 1);
    if (!firstRow) return replacements;

    if (rows.length !== 2 || firstRow.offset !== 0 || secondRow.offset !== 0) return replacements;

    const maxColumns = Math.min(firstRow.columns?.length ?? 0, secondRow.columns?.length ?? 0);
    for (let i = 0; i < maxColumns; i++) {
      const p1 = firstRow.columns[i]?.product;
      const p2 = secondRow.columns[i]?.product;
      if (p1?.ean && p1.ean === p2?.ean && p1.doubleProduct) {
        replacements.set(p1.ean, p1.doubleProduct);
      }
    }
    return replacements;
  }

  /**
   * Retourne tous les EANs liés à la composition (produits simples + doubleProducts)
   */
  private getCompositionRelatedEans(): Set<string> {
    const eans = new Set<string>();
    const rows = this.configurationState$.value.composition?.compositionConfiguration?.rows ?? [];
    for (const row of rows) {
      if (row.coverProduct?.ean) eans.add(row.coverProduct.ean);
      if (row.basinProduct?.ean) eans.add(row.basinProduct.ean);
      for (const column of row.columns ?? []) {
        if (column.product?.ean) eans.add(column.product.ean);
        if (column.product?.doubleProduct?.ean) eans.add(column.product.doubleProduct.ean);
        if (column.handle?.ean) eans.add(column.handle.ean);
      }
    }
    return eans;
  }

  /**
   * Ajoute ou incrémente un produit dans la map
   */
  private addOrUpdateProduct(map: Map<string, BathroomConfigurationProductDto>, product: BathroomProductDto): void {
    const existing = map.get(product.ean);
    if (existing) {
      existing.qty++;
    } else {
      map.set(product.ean, {
        ean: product.ean,
        label: product.label,
        salePrice: product.salePrice,
        range: product.bathroomRange?.name,
        qty: 1,
        imageSrc: product.thumbnailUrl
      });
    }
  }

  /**
   * Vérifie et corrige les images pour tous les produits en parallèle
   */
  private async resolveProductImages(products: BathroomConfigurationProductDto[]): Promise<void> {
    await Promise.all(products.map(p => this.resolveProductImage(p)));
  }

  /**
   * Pour un produit : teste l'URL actuelle, fallback via ProductApi, sinon null
   */
  private async resolveProductImage(product: BathroomConfigurationProductDto): Promise<void> {
    // 1. Tester l'URL existante
    if (product.imageSrc && (await this.isImageReachable(product.imageSrc))) return;

    // 2. Fallback : chercher via ProductApi par EAN
    const fallbackUrl = await this.fetchImageFromProductApi(product.ean);
    if (fallbackUrl && (await this.isImageReachable(fallbackUrl))) {
      product.imageSrc = fallbackUrl;
      return;
    }

    // 3. Aucune image valide → null (le placeholder est géré côté affichage)
    product.imageSrc = null;
  }

  private async isImageReachable(url: string): Promise<boolean> {
    const controller = new AbortController();
    try {
      const response = await fetch(url, { method: 'GET', signal: controller.signal });
      controller.abort(); // On n'a besoin que du status, pas du body
      return response.ok;
    } catch {
      return false;
    }
  }

  private async fetchImageFromProductApi(ean: string): Promise<string | null> {
    try {
      const response = await this.productApiService.sendSearchRequest({ searchTerm: ean, page: 1, sortBy: '' });
      const media = response?.data?.[0]?.attributes?.mediaObjects?.find((m: { url: string }) =>
        m.url.includes('image')
      );
      return media?.url?.replace('{width}', '300').replace('{height}', '300') ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Convertit un produit de configuration en PurchaseListItem
   */
  private toPurchaseListItem(product: BathroomConfigurationProductDto): PurchaseListItem {
    return {
      product: new ProductListItem({
        id: product.id,
        attributes: {
          ean: product.ean,
          name: product.label,
          mediaObjects: product.imageSrc ? [{ url: product.imageSrc, altText: '' }] : [],
          pricing: {
            currentPrice: {
              amountIncTax: product.salePrice ?? 0,
            },
          },
          averageRating: undefined,
          description: '',
          featureSummary: '',
          infoBullets: undefined,
          technicalSpecifications: undefined,
        }
      }),
      amount: product.qty,
    };
  }

  /**
   * Synchronise les produits depuis le PurchaseListStore vers bathroomConfigurationProducts
   * Permet de récupérer les produits ajoutés manuellement depuis l'écran purchase-list
   */
  syncProductsFromPurchaseList(): void {
    const purchaseListProducts = this._purchaseListStore.products();
    this.bathroomConfigurationProducts = purchaseListProducts.map(item => ({
      ean: item.product.ean,
      label: item.product.name,
      salePrice: item.product.price,
      qty: item.amount,
      imageSrc: item.product.imgPaths?.[0],
      range: item.product.brand,
      isFromConfiguration: item.wasManuallyAdded === false,
    }));

    // Recalculer le prix total à partir des produits synchronisés
    const totalPrice = this.bathroomConfigurationProducts.reduce(
      (sum, product) => sum + (product.salePrice ?? 0) * product.qty,
      0
    );

    // Mettre à jour le state avec le nouveau prix
    const currentState = this.configurationState$.value;
    this.configurationState$.next({
      ...currentState,
      totalPrice,
    });
  }

  /**
   * Réinitialise la liste d'achat en supprimant les produits issus de la configuration,
   * tout en conservant les produits ajoutés manuellement
   */
  resetConfigurationPurchaseList(): void {
    if (!this.bathroomConfigurationProducts.length) return;

    const compositionEans = this.getCompositionRelatedEans();

    // Ne garder que les produits qui ne font pas partie de la configuration
    this.bathroomConfigurationProducts = this.bathroomConfigurationProducts.filter(p => !compositionEans.has(p.ean));

    // Nettoyer aussi le store de la liste d'achat pour éviter que les anciens produits
    // soient considérés comme manuels lors du prochain sendToPurchaseList
    this._purchaseListStore.products.set(
      this._purchaseListStore.products().filter(item => !compositionEans.has(item.product.ean))
    );
  }

  public async createConfigurationAsync(): Promise<BathroomConfigurationDto> {
    const compositionConfig = this.configurationState$.value.composition?.compositionConfiguration;

    // Construction de l'objet à envoyer au backend pour créer la configuration
    const config: BathroomCreateUpdateConfigurationDto = {
      id: this.GUID_EMPTY,
      title: 'Ma salle de bain',
      products: this.bathroomConfigurationProducts,
      compositionId: this.configurationState$.value.composition?.id,
      compositionConfiguration: this.stripCompositionConfigurationIds(compositionConfig),
      thumbnail: { data: this.bathroomConfigurationSceneImage },
      isLocked: false,
    };

    // Appel au service pour créer la configuration
    return await this.bathroomService.createConfiguration(config);
  }

  public getPersistableCompositionConfiguration(): CompositionConfigurationDto | undefined {
    return this.stripCompositionConfigurationIds(this.configurationState$.value.composition?.compositionConfiguration);
  }

  /**
   * Vide les IDs des entités dans la configuration de composition avant envoi au backend
   */
  private stripCompositionConfigurationIds(
    config: CompositionConfigurationDto | undefined
  ): CompositionConfigurationDto | undefined {
    if (!config) return undefined;
    const { range: _range, handleConfigurationInfos: _handleConfigurationInfos, ...configuration } =
      config as CompositionConfigurationDto & { range?: unknown };

    return {
      ...configuration,
      id: this.GUID_EMPTY,
      handleConfigurationInfos: undefined,
      rows: configuration.rows.map(row => ({
        ...row,
        id: this.GUID_EMPTY,
        compositionConfigurationId: this.GUID_EMPTY,
        coverProduct: undefined,
        basinProduct: undefined,
        columns: row.columns.map(col => ({
          ...col,
          id: this.GUID_EMPTY,
          compositionRowId: this.GUID_EMPTY,
          productId: col.productId,
          handleId: col.handleId,
          product: undefined,
          handle: undefined,
        })),
      })),
    };
  }
}
